import { Injectable, inject } from '@angular/core'
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router'
import { Observable } from 'rxjs'
import { take, map, mergeMap } from 'rxjs/operators'

import { UserService } from '../services/user.service'
import { OidcSecurityService } from 'angular-auth-oidc-client'
import { preventPermissionsGuard, userData } from 'app/core/services/app-loader.service'
import { RoleTypes } from '../constants/role-types.enum'

/**
 * Checks the permissions of the user and whether it has access to the guarded route
 * */
@Injectable()
export class PermissionsGuard implements CanActivate {
    private userService = inject(UserService)
    private oidcSecurityService = inject(OidcSecurityService)
    private router = inject(Router)

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean> | boolean {
        if (userData.value == '' || preventPermissionsGuard.value) {
            preventPermissionsGuard.next(false)
            return true
        }

        return this.oidcSecurityService.isAuthenticated$.pipe(
            take(1),
            mergeMap(() => this.userService.loadUserProfile()),
            map((userProfile) => {
                if (userProfile != null) {
                    const goingToExists = localStorage.getItem('goingTo')

                    // Validation for public users going to an auction after been logged out due to session expiration
                    if (goingToExists && goingToExists.includes('/auctions/')) {
                        this.router.navigate([goingToExists])
                        if (
                            userProfile.role?.key !== RoleTypes.Buyer.toString() ||
                            userProfile.role?.key !== RoleTypes.Agent.toString()
                        ) {
                            localStorage.removeItem('goingTo')
                        }

                        return true
                    }

                    const userPermissions = userProfile.permissions!.map((p) => p.key)
                    const requiredPermissions: string[] = route.data['permissions']

                    for (let i = 0; i < requiredPermissions.length; i++) {
                        const currentPermission = requiredPermissions[i]

                        if (userPermissions.indexOf(currentPermission) === -1) {
                            this.router.navigate(['forbidden'])
                            return true
                        }
                    }
                    return true
                }
                return true
            })
        )
    }
}
